import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DealDto } from '@78/shared';
import { isInDefault, refundPercentOf, type DealTerms } from '@78/core';
import { CURRENCY } from '@78/i18n';
import { useTonAddress } from '@tonconnect/ui-react';
import { fetchPositions } from '../lib/api.ts';
import { useErrorText } from '../lib/errors.ts';
import { sameAddress, shortAddress } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';
import { GiftArt } from '../components/GiftArt.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useActions } from '../lib/actions.ts';
import { reportFailure } from '../lib/report.ts';

function toTerms(d: DealDto): DealTerms {
  return {
    totalPrice: BigInt(d.terms.totalPrice),
    upfront: BigInt(d.terms.upfront),
    deferFee: BigInt(d.terms.deferFee),
    period: d.terms.period,
    nPayments: d.terms.nPayments,
    refundTier: d.terms.refundTier,
    maxDeferrals: d.terms.maxDeferrals,
    feeBps: d.terms.feeBps,
    deadline: d.terms.deadline,
  };
}

/** Сколько ещё придёт владельцу позиции за вычетом комиссии. */
function remaining(d: DealDto, terms: DealTerms): bigint {
  const paid = BigInt(d.progress.paidTotal);
  const fee = (terms.totalPrice * BigInt(terms.feeBps)) / 10000n;
  const got = paid - BigInt(d.progress.retained) - (paid * BigInt(terms.feeBps)) / 10000n;
  return terms.totalPrice - fee - got;
}

type Sort = 'price' | 'yield' | 'soon' | 'risk';

/**
 * Витрина контрактов.
 *
 * Здесь только то, что владелец сам выставил на продажу: раньше раздел
 * показывал все идущие сделки подряд и называл это продажей, хотя выставить
 * позицию было нельзя вовсе. Теперь цена живёт в контракте, а обмен делает
 * он же — деньги уходят владельцу той же транзакцией, которой позиция
 * переходит к покупателю.
 *
 * Показываем то, по чему такую позицию оценивают: цену, сколько ещё придёт,
 * когда ближайший платёж и как покупатель платил до сих пор.
 */
function PositionCard({ deal, mine }: { deal: DealDto; mine: boolean }) {
  const { t } = useTranslation();
  const { sum, money, dateTime } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const connected = actions.connected;

  const buy = async () => {
    setBusy(true);
    setErr(null);
    try {
      await actions.buyPosition({
        dealAddress: deal.address,
        price: BigInt(deal.askPrice ?? '0'),
      });
    } catch (e) {
      reportFailure('выкуп позиции', e);
      const text = errorText(e);
      if (text.length > 0) setErr(text);
    } finally {
      setBusy(false);
    }
  };

  const terms = toTerms(deal);
  const paid = BigInt(deal.progress.paidTotal);
  const now = Math.floor(Date.now() / 1000);
  const late = isInDefault(terms, deal.progress.startTs, paid, now);
  const installment = (terms.totalPrice - terms.upfront) / BigInt(terms.nPayments);
  const done = installment > 0n ? Number((paid - terms.upfront) / installment) : 0;

  return (
    <div className="card col" style={{ padding: 13, gap: 11 }}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
        {/* Имя и картинка гифта у нас теперь есть — адрес вместо названия
            читался как поломка, а выбирают такие вещи глазами. */}
        <GiftArt image={deal.nftImage} size={48} radius={11} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {deal.nftName ?? shortAddress(deal.nftAddress)}
          </div>
          <div className="label" style={{ fontSize: 10, marginTop: 3, letterSpacing: 0 }}>
            {t('positions.in_escrow_label')} · {String(refundPercentOf(terms.refundTier))}%
          </div>
        </div>
        <span className={late ? 'chip chip-dng' : 'chip'}>
          {late ? t('positions.discipline_late') : t('positions.discipline_ok')}
        </span>
      </div>

      <div className="stats3" style={{ gap: 7 }}>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('positions.left_to_receive')}</div>
          <div className="stat-value lime" style={{ fontSize: 12.5 }}>{money(remaining(deal, terms))}</div>
        </div>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('positions.payments_done')}</div>
          <div className="stat-value" style={{ fontSize: 12.5 }}>
            {Math.max(done, 0)}/{terms.nPayments}
          </div>
        </div>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('buyer.safe_pay_until')}</div>
          <div className="stat-value" style={{ fontSize: 12.5 }}>
            {deal.progress.payBefore === null ? '—' : dateTime(deal.progress.payBefore).split(',')[0]}
          </div>
        </div>
      </div>

      <div className="row">
        <span className="label">{t('positions.owner')}</span>
        <span className="mono" style={{ fontSize: 11 }}>{shortAddress(deal.seller)}</span>
      </div>

      {/* Цена и выкуп. Обмен делает контракт: деньги уходят владельцу той же
          транзакцией, которой позиция переходит к плательщику. */}
      <div className="row">
        <span className="text" style={{ fontWeight: 700 }}>{t('positions.price', { cur: CURRENCY })}</span>
        <span className="mono num lime" style={{ fontSize: 15, fontWeight: 700 }}>
          {sum(deal.askPrice ?? '0')}
        </span>
      </div>
      {err !== null && <div className="danger" style={{ fontSize: 12 }}>{err}</div>}
      {mine ? (
        <span className="label">{t('positions.mine')}</span>
      ) : (
        <button
          className="btn btn-sm"
          disabled={busy || !connected}
          onClick={() => setConfirm(true)}
        >
          {busy ? '…' : `${t('positions.buy')} · ${sum(deal.askPrice ?? '0')}`}
        </button>
      )}

      {confirm && (
        <Sheet
          title={t('positions.buy')}
          confirmLabel={`${t('positions.buy')} · ${sum(deal.askPrice ?? '0')}`}
          busy={busy}
          onClose={() => setConfirm(false)}
          onConfirm={() => {
            setConfirm(false);
            void buy();
          }}
        >
          <span className="text">{t('positions.buy_hint')}</span>
        </Sheet>
      )}
    </div>
  );
}

export function Positions() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [sort, setSort] = useState<Sort>('price');
  const myAddress = useTonAddress(false);
  const { data, isLoading, error } = useQuery({ queryKey: ['positions'], queryFn: fetchPositions });

  // Здесь только выставленное на продажу — это решает сервер. Свою позицию
  // видно тоже: продавец должен видеть свой лот там же, где его видят
  // покупатели, — но выкупить её у себя нельзя.
  const rows = [...(data?.deals ?? [])].sort((a, b) => {
    const ta = toTerms(a);
    const tb = toTerms(b);
    if (sort === 'soon') return (a.progress.payBefore ?? 0) - (b.progress.payBefore ?? 0);
    if (sort === 'risk') {
      const now = Math.floor(Date.now() / 1000);
      const la = isInDefault(ta, a.progress.startTs, BigInt(a.progress.paidTotal), now) ? 1 : 0;
      const lb = isInDefault(tb, b.progress.startTs, BigInt(b.progress.paidTotal), now) ? 1 : 0;
      return la - lb;
    }
    if (sort === 'price') return Number(BigInt(a.askPrice ?? '0') - BigInt(b.askPrice ?? '0'));
    return Number(remaining(b, tb) - remaining(a, ta));
  });

  return (
    <div className="col" style={{ gap: 13 }}>
      <div>
        <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {t('positions.title_full')}
        </div>
        <div className="text" style={{ marginTop: 4 }}>{t('positions.subtitle_full')}</div>
      </div>

      <div className="chips">
        {(['price', 'yield', 'soon', 'risk'] as const).map((s) => (
          <button key={s} aria-selected={sort === s} onClick={() => setSort(s)}>
            {s === 'price'
              ? t('positions.price')
              : s === 'yield'
                ? t('positions.sort_yield')
                : s === 'soon'
                  ? t('positions.sort_soon')
                  : t('positions.sort_risk')}
          </button>
        ))}
      </div>

      {isLoading && <div className="skeleton" style={{ minHeight: 110 }} />}
      {error !== null && error !== undefined && (
        <div className="card card-dng text">{errorText(error)}</div>
      )}
      {!isLoading && rows.length === 0 && <div className="card text">{t('positions.empty')}</div>}

      {rows.map((d) => (
        <PositionCard
          key={d.address}
          deal={d}
          mine={myAddress.length > 0 && sameAddress(d.seller, myAddress)}
        />
      ))}

      {rows.length > 0 && <span className="text" style={{ fontSize: 11.5 }}>{t('positions.contact_seller')}</span>}
    </div>
  );
}
